import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Plus, RefreshCw, Trash2, Mail, Globe } from 'lucide-react';
import { getExternalAccounts, deleteExternalAccount, syncExternalAccount, getSyncStatus } from '../../services/emailApi';

export default function AccountSwitcher({ currentAccount, onAccountChange, onAddAccount, inline }) {
  const [accounts, setAccounts] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [syncing, setSyncing] = useState({});
  const dropdownRef = useRef(null);

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    if (inline) return;
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inline]);

  async function loadAccounts() {
    try {
      const data = await getExternalAccounts();
      setAccounts(data.accounts || []);
    } catch (err) {
      console.error('Failed to load external accounts:', err);
    }
  }

  async function handleSync(accountId, e) {
    e.stopPropagation();
    setSyncing(prev => ({ ...prev, [accountId]: true }));
    try {
      await syncExternalAccount(accountId);
      const poll = setInterval(async () => {
        try {
          const { status } = await getSyncStatus(accountId);
          if (!status.isRunning) {
            clearInterval(poll);
            setSyncing(prev => ({ ...prev, [accountId]: false }));
            loadAccounts();
          }
        } catch {
          clearInterval(poll);
          setSyncing(prev => ({ ...prev, [accountId]: false }));
        }
      }, 2000);
    } catch {
      setSyncing(prev => ({ ...prev, [accountId]: false }));
    }
  }

  async function handleDelete(accountId, e) {
    e.stopPropagation();
    if (!confirm('Remove this account? All synced emails will be deleted.')) return;
    try {
      await deleteExternalAccount(accountId);
      setAccounts(prev => prev.filter(a => a.id !== accountId));
      if (currentAccount === accountId) {
        onAccountChange('all');
      }
    } catch (err) {
      console.error('Failed to delete account:', err);
    }
  }

  // Render the account list items (shared between inline and dropdown modes)
  function renderItems() {
    return (
      <>
        {/* All Accounts */}
        <button
          onClick={() => { onAccountChange('all'); if (!inline) setIsOpen(false); }}
          className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 ${currentAccount === 'all' ? 'bg-indigo-50 text-indigo-600' : ''}`}
        >
          <Globe size={14} />
          <span>All Accounts</span>
        </button>

        {/* Internal (Jubly) Account */}
        <button
          onClick={() => { onAccountChange('internal'); if (!inline) setIsOpen(false); }}
          className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 ${currentAccount === 'internal' ? 'bg-indigo-50 text-indigo-600' : ''}`}
        >
          <Mail size={14} className="text-indigo-500" />
          <span>Jubly Mail</span>
        </button>

        {/* External Accounts */}
        {accounts.map(account => (
          <div
            key={account.id}
            onClick={() => { onAccountChange(account.id); if (!inline) setIsOpen(false); }}
            className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer ${currentAccount === account.id ? 'bg-indigo-50 text-indigo-600' : ''}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Mail size={14} className="text-green-500 shrink-0" />
              <div className="min-w-0">
                <div className="truncate">{account.label || account.external_email}</div>
                {account.last_sync_at && (
                  <div className="text-xs text-gray-400 truncate">
                    Synced {new Date(account.last_sync_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0 ml-2">
              <button
                onClick={(e) => handleSync(account.id, e)}
                className="p-1 hover:bg-gray-200 rounded"
                title="Sync"
                disabled={syncing[account.id]}
              >
                <RefreshCw size={12} className={syncing[account.id] ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={(e) => handleDelete(account.id, e)}
                className="p-1 hover:bg-red-100 rounded text-red-500"
                title="Remove"
              >
                <Trash2 size={12} />
              </button>
            </div>
          </div>
        ))}

        {/* Add Account */}
        <div className="border-t border-gray-100 my-1" />
        <button
          onClick={() => { onAddAccount(); if (!inline) setIsOpen(false); }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50"
        >
          <Plus size={14} />
          <span>Add Account</span>
        </button>
      </>
    );
  }

  // Inline mode: just render items directly (used inside Sidebar popover)
  if (inline) {
    return <div>{renderItems()}</div>;
  }

  // Dropdown mode (standalone)
  const currentLabel = currentAccount === 'all' ? 'All Accounts'
    : currentAccount === 'internal' ? 'Jubly Mail'
    : accounts.find(a => a.id === currentAccount)?.label || currentAccount;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Mail size={14} className="shrink-0 text-gray-500" />
          <span className="truncate font-medium">{currentLabel}</span>
        </div>
        <ChevronDown size={14} className={`shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
          {renderItems()}
        </div>
      )}
    </div>
  );
}
